import React, { useState, useEffect } from 'react';
import { AppSection } from '../types';

interface CaseStudiesProps {
  onNavigate: (section: AppSection) => void;
  onContactClick: () => void;
}

const CaseStudies: React.FC<CaseStudiesProps> = ({ onNavigate, onContactClick }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate high-latency enterprise data fetch
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const studies = [
    {
      client: "FinTech Global",
      title: "Cloud-Native Infrastructure Modernization",
      metric: "40% Ops Savings",
      tags: ["AWS", "Kubernetes", "DevOps"],
      summary: "Modernized a legacy monolith into a high-availability microservices architecture, enabling daily deployment cycles."
    },
    {
      client: "LogiChain Systems",
      title: "AI-Driven Predictive Logistics Engine",
      metric: "12% Route Efficiency",
      tags: ["Python", "TensorFlow", "GCP"],
      summary: "Developed a custom ML model to optimize freight routing across North American networks in real-time."
    },
    {
      client: "NeoHealth Corp",
      title: "HIPAA-Compliant Patient Portal Scaling",
      metric: "3.2M Active Users",
      tags: ["React", "Node.js", "PostgreSQL"],
      summary: "Scaled a healthcare application to handle massive traffic spikes during peak enrollment periods with zero downtime."
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      <section className="bg-[#020617] py-32 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-dark opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="mono text-xs text-[#facc15] font-bold mb-6 tracking-widest uppercase">// Technical Proof</div>
            <h1 className="text-6xl md:text-8xl font-bold mb-8 tracking-tighter leading-[0.9]">Validated Outcomes</h1>
            <p className="text-xl text-slate-400 font-light leading-relaxed max-w-2xl">
              Concrete evidence of Hunargrid velocity. We don't just ship code; we deliver business transformation through architectural excellence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // High-fidelity Skeleton Screens
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-100 p-10 rounded-sm">
                  <div className="flex justify-between items-start mb-8">
                    <div className="h-3 w-24 skeleton rounded"></div>
                    <div className="h-6 w-20 skeleton rounded-full"></div>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="h-6 w-full skeleton rounded"></div>
                    <div className="h-6 w-3/4 skeleton rounded"></div>
                  </div>
                  <div className="space-y-2 mb-10">
                    <div className="h-3 w-full skeleton rounded opacity-50"></div>
                    <div className="h-3 w-full skeleton rounded opacity-50"></div>
                    <div className="h-3 w-2/3 skeleton rounded opacity-50"></div>
                  </div>
                  <div className="flex gap-2 pt-8 border-t border-slate-50">
                    <div className="h-6 w-12 skeleton rounded"></div>
                    <div className="h-6 w-16 skeleton rounded"></div>
                    <div className="h-6 w-14 skeleton rounded"></div>
                  </div>
                </div>
              ))
            ) : (
              studies.map((study, i) => (
                <div key={i} className="bg-white border border-slate-100 p-10 hover:shadow-2xl hover:shadow-[#0091da]/5 transition-all group rounded-sm animate-in fade-in duration-700" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="flex justify-between items-start mb-8">
                    <div className="mono text-[10px] text-slate-400 uppercase tracking-widest">{study.client}</div>
                    <div className="bg-[#0091da]/10 text-[#0091da] text-[10px] font-bold px-3 py-1 rounded-full">{study.metric}</div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-950 mb-6 group-hover:text-[#0091da] transition-colors tracking-tight">{study.title}</h3>
                  <p className="text-slate-500 text-sm font-light leading-relaxed mb-10">{study.summary}</p>
                  <div className="flex flex-wrap gap-2 pt-8 border-t border-slate-50">
                    {study.tags.map((tag, j) => (
                      <span key={j} className="text-[9px] font-bold text-slate-400 uppercase tracking-widest border border-slate-100 px-3 py-1">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">Need specific technical evidence?</h2>
          <p className="text-slate-500 mb-10 font-light">We have a deep repository of technical white papers and architectural reviews available for qualified enterprise inquiries.</p>
          <button 
            onClick={onContactClick}
            className="btn-brand-primary px-12 py-5 font-bold uppercase tracking-widest text-xs rounded-sm"
          >
            Request Tech Portfolio
          </button>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;